/**
 * Pure date model for the customer booking calendar. All cross-month range
 * correctness lives here so the dual-month <BookingCalendar> components can stay
 * thin and just render what these functions return. Everything is computed from
 * 'YYYY-MM-DD' strings in UTC (no local-timezone drift), and ISO strings compare
 * lexicographically, so `a <= b` is a valid date comparison.
 *
 * Booking-math contract: `Selected.end` is the INCLUSIVE drop-off date the
 * customer tapped — both the pickup and drop-off day are charged and blocked
 * (daily rental-car model). `cellState.inRange` is inclusive of `end` to match.
 * The DB stores half-open `[start, end_exclusive)`; the API edge converts the
 * inclusive drop-off to that boundary via `toExclusiveEnd` (see ./bookingDays),
 * so the proven overlap SQL is untouched. Inclusive day count = `bookingDays`.
 */

export type DayAvailability = { date: string; remaining: number } // YYYY-MM-DD
export type Selected = { start: string | null; end: string | null }
type MonthCell = { iso: string; dayNum: number; remaining: number }
export type MonthGridModel = {
  monthStart: string
  label: string
  leadingBlanks: number // empty cells before day 1 (0 = Sun … 6 = Sat)
  cells: MonthCell[]
}
export type CellState = {
  isStart: boolean
  isEnd: boolean
  inRange: boolean
  isPreview: boolean
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** Advance `monthStart` ('YYYY-MM-01') by `n` months, pinned to day 1. */
export function addMonths(monthStart: string, n: number): string {
  const [y, m] = monthStart.split('-').map(Number)
  const total = y * 12 + (m - 1) + n
  const ny = Math.floor(total / 12)
  const nm = ((total % 12) + 12) % 12 + 1
  return `${ny}-${String(nm).padStart(2, '0')}-01`
}

/** Build one month's grid: label, leading blanks, and a cell per day. */
export function buildMonthGrid(monthStart: string, days: DayAvailability[]): MonthGridModel {
  const [y, m] = monthStart.split('-').map(Number)
  const byDate = new Map(days.map(d => [d.date, d.remaining]))
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate()
  const leadingBlanks = new Date(Date.UTC(y, m - 1, 1)).getUTCDay()
  const cells: MonthCell[] = []
  for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
    const iso = `${y}-${String(m).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`
    cells.push({ iso, dayNum, remaining: byDate.get(iso) ?? 0 })
  }
  return { monthStart, label: `${MONTHS[m - 1]} ${y}`, leadingBlanks, cells }
}

export type DayState = 'past' | 'soldout' | 'available'

/**
 * The single source of truth for a day cell's appearance. Precedence: a day
 * before `minDate` is 'past' regardless of units (the calendar RPCs do NOT
 * floor past days — the floor must live client-side); otherwise fewer units
 * than the requested qty is 'soldout'; otherwise 'available'.
 */
export function dayState(iso: string, remaining: number, qty: number, minDate: string): DayState {
  if (iso < minDate) return 'past'
  if (remaining < qty) return 'soldout'
  return 'available'
}

/**
 * A day cell is non-selectable unless it's available. Defined in terms of
 * dayState so the disabled predicate and the visual state can never drift.
 */
export function dayDisabled(iso: string, remaining: number, qty: number, minDate: string): boolean {
  return dayState(iso, remaining, qty, minDate) !== 'available'
}

export type MonthWindow = { minMonth: string; maxMonth: string }

/**
 * Rolling 12-month booking horizon (inclusive both ends). The availability
 * RPCs have no horizon of their own — this client-side bound keeps the year
 * dropdown and pagers from stranding a visitor in an empty far-future month.
 */
/**
 * 'YYYY-MM-DD' → the 'YYYY-MM-01' that month starts on. Pure string work on
 * purpose: this is how the booking forms pick their opening month from the
 * `today` the SERVER hands them (already in the location's zone), so the
 * server build and the browser build open on the same month. Deriving it from
 * `new Date()` in the form put the server (UTC) a month ahead of a US visitor
 * for the last few hours of every month — a hydration mismatch that made React
 * rebuild the page and duplicate its inline JSON-LD.
 */
export function monthStartOfYmd(ymd: string): string {
  return ymd.slice(0, 8) + '01'
}

export function monthWindow(minDate: string): MonthWindow {
  const minMonth = monthStartOfYmd(minDate)
  return { minMonth, maxMonth: addMonths(minMonth, 12) }
}

/** Clamp a 'YYYY-MM-01' month into the window (ISO strings compare lexicographically). */
export function clampMonth(monthStart: string, w: MonthWindow): string {
  if (monthStart < w.minMonth) return w.minMonth
  if (monthStart > w.maxMonth) return w.maxMonth
  return monthStart
}

export type MonthOption = { value: string; label: string; disabled: boolean }

/**
 * The month dropdown's 12 entries for one calendar year. Out-of-window months
 * are disabled rather than omitted so every month keeps its slot.
 */
export function monthOptions(year: number, w: MonthWindow): MonthOption[] {
  return MONTHS.map((label, i) => {
    const value = `${year}-${String(i + 1).padStart(2, '0')}-01`
    return { value, label, disabled: value < w.minMonth || value > w.maxMonth }
  })
}

/** Each calendar year the window touches (always 2 with a +12-month window). */
export function yearOptions(w: MonthWindow): number[] {
  const first = Number(w.minMonth.slice(0, 4))
  const last = Number(w.maxMonth.slice(0, 4))
  const out: number[] = []
  for (let y = first; y <= last; y++) out.push(y)
  return out
}

/** 'August' from '2026-08-01' — for the empty-month notice. */
export function monthName(monthStart: string): string {
  return MONTHS[Number(monthStart.slice(5, 7)) - 1]
}

/**
 * Selection state for one day cell. `hover` drives the live range preview while
 * the customer is choosing the end (start set, end null). Once both endpoints
 * exist, hover is ignored.
 */
export function cellState(iso: string, selected: Selected, hover: string | null): CellState {
  const { start, end } = selected
  const isStart = start != null && iso === start
  const isEnd = end != null && iso === end
  const inRange = start != null && end != null && iso >= start && iso <= end
  const isPreview =
    start != null && end == null && hover != null && iso >= start && iso <= hover
  return { isStart, isEnd, inRange, isPreview }
}
